import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Radio,
  Input,
  Select,
  Upload,
  Alert,
  Progress,
  Space,
  Spin,
  Tag,
  Tooltip,
} from "antd";
import {
  InboxOutlined,
  AimOutlined,
  ReloadOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import Cookies from "universal-cookie";
import { toast } from "react-toastify";

const { Option } = Select;

// LocalStorage key helpers
const keyForProgress = (tranId) => `surveyProgress:${tranId}`;

export default function SurveyWizardModal({
  open,
  onClose,
  tranId, // required
  adminOverrideAllowed = false,
  onAllDone,
}) {
  const cookies = new Cookies();
  const token = cookies.get("token");
  const cookieUserId = cookies.get("userId");
  const authHeaders = { Authorization: `Bearer ${token}` };

  // ---------- global ----------
  const [loadingInit, setLoadingInit] = useState(true);
  const [wo, setWo] = useState(null);
  const [survey, setSurvey] = useState(null); // {_id, customSurvey:[...]}
  const [tasks, setTasks] = useState([]);
  const [totalTasksCount, setTotalTasksCount] = useState(0); // server-reported total (info only)
  const [taskIdx, setTaskIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [initialIncompleteCount, setInitialIncompleteCount] = useState(0); // baseline for progress

  const isWOCompleted = wo?.reconciliationStatus === "Completed";

  // ---------- per-task ----------
  const [path, setPath] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // for current task only
  const [answersByTask, setAnswersByTask] = useState({}); // { [taskId]: { answers, qIdx, path } }
  const [adminOverride, setAdminOverride] = useState(false);

  // cache last geo reading
  const [sessionGeo, setSessionGeo] = useState(null); // {lat, lng, ts}

  const currentTask = tasks[taskIdx];
  const totalTasks = tasks.length; // current incomplete count (for progress)
  const getTaskId = (t) => t?._id || t?.id || t?.taskId;

  // ---------- userId fallback ----------
  const getUserIdValue = useCallback(() => {
    if (cookieUserId) return cookieUserId;
    try {
      const raw = localStorage.getItem("userData");
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed?.user?.id || parsed?.id || "";
      }
    } catch { }
    return "";
  }, [cookieUserId]);

  // ---------- normalizers ----------
  const normalizeTasksPayload = (payload, { surveyMode = false } = {}) => {
    const d = payload?.data ?? payload;
    if (Array.isArray(d?.tasks)) return d.tasks;

    const inc = Array.isArray(d?.taskInCompleted) ? d.taskInCompleted : [];
    const comp = Array.isArray(d?.taskCompleted) ? d.taskCompleted : [];

    if (surveyMode) return inc; // only incomplete for survey
    if (inc.length || comp.length) return [...inc, ...comp];
    if (Array.isArray(payload)) return payload;
    return [];
  };

  const normalizeSurveyTemplate = (payload) => {
    const list = Array.isArray(payload?.data?.list) ? payload.data.list : [];
    const item = list[0];
    if (!item) return null;
    return {
      _id: item._id,
      title: item.title,
      jobType: item.jobType,
      customSurvey: Array.isArray(item.customSurvey) ? item.customSurvey : [],
    };
  };

  // ---------- loaders ----------
  const fetchWorkOrder = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/workOrders/tranId/${tranId}`,
      { headers: authHeaders }
    );
    return res.data?.workOrder || res.data?.data || res.data;
  };

  // paginate through all pages to get ALL incomplete tasks
  const fetchAllSurveyTasks = async () => {
    let url = `${process.env.REACT_APP_API}/task/task-by-tran-id/${tranId}?mode=survey&perPage=50&page=1`;
    let all = [];
    let total = 0;
    const seen = new Set();

    while (url) {
      const res = await axios.get(url, { headers: authHeaders });
      const data = res.data || {};
      const chunk = normalizeTasksPayload(data, { surveyMode: true });
      total = data.totalTasks || total;

      // Filter to isCompleted === false, dedupe by id
      for (const t of chunk || []) {
        if (t && t.isCompleted === false) {
          const id = getTaskId(t);
          if (!seen.has(id)) {
            seen.add(id);
            all.push(t);
          }
        }
      }
      url = data?.next_page_url || null;
    }
    return { tasks: all, total };
  };

  const loadSurveyTemplate = async (jt) => {
    const ck = `survey:${tranId}:${jt}`;
    try {
      const cached = sessionStorage.getItem(ck);
      if (cached) {
        const payload = { data: { list: JSON.parse(cached) } };
        const spec = normalizeSurveyTemplate(payload);
        if (spec) return spec;
      }
    } catch { }
    const res = await axios.post(
      `${process.env.REACT_APP_API}/surveyTemplate/searchSurveyTemplate`,
      { jobType: jt },
      { headers: authHeaders }
    );
    const spec = normalizeSurveyTemplate(res.data);
    try {
      const rawList = Array.isArray(res?.data?.data?.list) ? res.data.data.list : [];
      sessionStorage.setItem(ck, JSON.stringify(rawList));
    } catch { }
    return spec;
  };

  // ---------- geotag ----------
  const getPosition = (opts) =>
    new Promise((resolve, reject) => {
      if (!("geolocation" in navigator)) {
        return reject(new Error("Geolocation API not available"));
      }
      navigator.geolocation.getCurrentPosition(resolve, reject, opts);
    });

  const captureGeotag = async () => {
    try {
      const pos = await getPosition({
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0,
      });
      const geo = { lat: pos.coords.latitude, lng: pos.coords.longitude, ts: Date.now() };
      setSessionGeo(geo);
      return geo;
    } catch {
      const pos = await getPosition({
        enableHighAccuracy: false,
        timeout: 25000,
        maximumAge: 60000,
      });
      const geo = { lat: pos.coords.latitude, lng: pos.coords.longitude, ts: Date.now() };
      setSessionGeo(geo);
      return geo;
    }
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      try { const g = await captureGeotag(); setSessionGeo(g); } catch { }
    })();
    // eslint-disable-next-line
  }, [open]);

  // ---------- branching helpers ----------
  // Strong initial path from a template object (avoids race with setSurvey)
  const computeInitialPathFromTemplate = (tmpl) => {
    const items = Array.isArray(tmpl?.customSurvey) ? tmpl.customSurvey : [];
    if (!items.length) return [];
    const targeted = new Set();
    items.forEach((q) => (q?.conditions || []).forEach((c) => c?.questionId && targeted.add(c.questionId)));
    const roots = items.filter((q) => q?.uuid && !targeted.has(q.uuid));
    const use = roots.length ? roots : [items[0]]; // fallback to first question
    return use.map((q) => q.uuid);
  };

  // ---------- branching engine ----------
  const byId = useMemo(() => {
    const map = new Map();
    (survey?.customSurvey || []).forEach((q) => map.set(q.uuid, q));
    return map;
  }, [survey]);

  const incomingTargets = useMemo(() => {
    const set = new Set();
    (survey?.customSurvey || []).forEach((q) =>
      (q.conditions || []).forEach((c) => c?.questionId && set.add(c.questionId))
    );
    return set;
  }, [survey]);

  const roots = useMemo(() => {
    const arr = (survey?.customSurvey || []).filter(
      (q) => !incomingTargets.has(q.uuid)
    );
    return arr.length ? arr : (survey?.customSurvey || []).slice(0, 1);
  }, [survey, incomingTargets]);

  const orderIndex = useMemo(() => {
    const idx = new Map();
    (survey?.customSurvey || []).forEach((q, i) => idx.set(q.uuid, i));
    return idx;
  }, [survey]);

  const completedSoFar = useMemo(() => {
    const initial = initialIncompleteCount || 0;
    const remaining = totalTasks || 0; // current incomplete
    return Math.max(0, initial - remaining);
  }, [initialIncompleteCount, totalTasks]);

  const conditionSatisfied = (sourceQuestion, cond, answerMap) => {
    const srcUuid = sourceQuestion?.uuid;
    const srcAns = answerMap[srcUuid]?.answer ?? "";
    const state = String(cond?.state || "").toLowerCase();
    const cmp = cond?.value;

    if (state === "is equal to") return String(srcAns) === String(cmp);
    if (state === "filled") {
      if (sourceQuestion?.type === "image") {
        return !!answerMap[srcUuid]?.file || !!answerMap[srcUuid]?.answer;
      }
      return String(srcAns || "").trim().length > 0;
    }
    return false;
  };

  const getTriggeredTargets = (question, answerMap) => {
    const outs = [];
    (question?.conditions || []).forEach((c) => {
      if (c?.questionId && conditionSatisfied(question, c, answerMap)) {
        outs.push(c.questionId);
      }
    });
    outs.sort((a, b) => (orderIndex.get(a) ?? 0) - (orderIndex.get(b) ?? 0));
    return outs;
  };

  const buildPathFromAnswers = (answerMap) => {
    // BFS through triggered edges, respecting template order
    const queue = [];
    const seen = new Set();

    const pushIfNew = (uuid) => {
      if (!uuid || seen.has(uuid) || !byId.get(uuid)) return;
      seen.add(uuid);
      queue.push(uuid);
    };

    // start from roots (fallback to first in template if none)
    const startItems =
      roots && roots.length
        ? roots
        : Array.isArray(survey?.customSurvey) && survey.customSurvey.length
          ? [survey.customSurvey[0]]
          : [];
    startItems.forEach((r) => pushIfNew(r.uuid || r));

    for (let i = 0; i < queue.length; i++) {
      const uuid = queue[i];
      const q = byId.get(uuid);
      if (!q) continue;

      const nexts = getTriggeredTargets(q, answerMap);
      nexts.forEach((n) => pushIfNew(n));
    }

    return queue;
  };

  // ---------- persistence ----------
  const persistProgress = useCallback(
    (nextAnswersByTask) => {
      if (!tranId) return;
      const snapshot = {
        taskIdx,
        taskId: getTaskId(currentTask),
        answersByTask: nextAnswersByTask,
        adminOverride,
        tasksSummary: tasks.map((t) => ({
          id: getTaskId(t),
          title: t?.title,
          isCompleted: t?.isCompleted,
        })),
        updatedAt: Date.now(),
        initialIncompleteCount, // ✅ include baseline
      };
      try {
        localStorage.setItem(keyForProgress(tranId), JSON.stringify(snapshot));
      } catch { }
    },
    [tranId, taskIdx, currentTask, adminOverride, tasks, initialIncompleteCount]
  );

  // Keep answersByTask in sync with the live answers for the current task
  useEffect(() => {
    if (!currentTask) return;
    const tid = getTaskId(currentTask);
    const next = {
      ...answersByTask,
      [tid]: { answers, qIdx, path },
    };
    setAnswersByTask(next);
    persistProgress(next);
    // eslint-disable-next-line
  }, [answers, qIdx, JSON.stringify(path), currentTask]);

  // ---------- bootstrap ----------
  const init = useCallback(async () => {
    setLoadingInit(true);
    try {
      const woResp = await fetchWorkOrder();
      setWo(woResp);
      if (woResp?.reconciliationStatus === "Completed") {
        toast.info("Work order has been completed");
      }

      const jt = woResp?.jobType || woResp?.job_type || woResp?.job;
      const [spec, { tasks: allTasks, total }] = await Promise.all([
        jt ? loadSurveyTemplate(jt) : Promise.resolve(null),
        fetchAllSurveyTasks(),
      ]);
      setSurvey(spec);
      setTasks(allTasks);
      setTotalTasksCount(total || allTasks.length || 0);

      // establish baseline incomplete count (restore if present)
      let restored = null;
      try {
        const raw = localStorage.getItem(keyForProgress(tranId));
        if (raw) restored = JSON.parse(raw);
      } catch { }
      const initialCnt =
        Number.isFinite(restored?.initialIncompleteCount) && restored.initialIncompleteCount > 0
          ? restored.initialIncompleteCount
          : (allTasks?.length || 0);
      setInitialIncompleteCount(initialCnt);

      // compute initial path directly from fetched template to avoid race
      const initialPath = computeInitialPathFromTemplate(spec);

      if (restored && Array.isArray(allTasks) && allTasks.length) {
        const idx = allTasks.findIndex((t) => getTaskId(t) === restored.taskId);
        const safeIdx = idx >= 0 ? idx : 0;
        setTaskIdx(safeIdx);
        setAnswersByTask(restored.answersByTask || {});
        setAdminOverride(!!restored.adminOverride);

        const saved = (restored.answersByTask || {})[getTaskId(allTasks[safeIdx])] || {};
        setAnswers(saved.answers || {});
        setPath(saved.path && saved.path.length ? saved.path : initialPath);
        setQIdx(Number.isFinite(saved.qIdx) ? saved.qIdx : 0);
      } else {
        // fresh start
        setAnswers({});
        setPath(initialPath);
        setQIdx(0);
      }
    } catch {
      toast.error("Failed to initialize survey");
      setTasks([]);
      setAnswers({});
      setPath([]);
      setQIdx(0);
      setInitialIncompleteCount(0);
    } finally {
      setLoadingInit(false);
    }
  }, [tranId]);

  useEffect(() => {
    if (open) init();
  }, [open, init]);

  // when task changes, load saved answers for that task if present
  useEffect(() => {
    if (!open || !currentTask) return;
    const tid = getTaskId(currentTask);
    const saved = answersByTask[tid];
    if (saved) {
      // images cannot survive refresh; we keep text/dropdown
      const restoredAnswers = { ...(saved.answers || {}) };
      Object.values(restoredAnswers).forEach((a) => {
        if (a?.type === "image") {
          delete a.file;
          if (a.previewUrl) {
            try { URL.revokeObjectURL(a.previewUrl); } catch { }
          }
          a.previewUrl = "";
          if (a.answer) a.answer = "";
          a.needsReattach = true;
        }
      });
      setAnswers(restoredAnswers);
      setPath(saved.path && saved.path.length ? saved.path : computeInitialPathFromTemplate(survey));
      setQIdx(Number.isFinite(saved.qIdx) ? saved.qIdx : 0);
    } else {
      setAnswers({});
      setPath(computeInitialPathFromTemplate(survey));
      setQIdx(0);
    }
    // eslint-disable-next-line
  }, [taskIdx, open]);

  // ---------- interactions ----------
  const currentUuid = path[qIdx];
  const currentQuestion = useMemo(() => byId.get(currentUuid), [byId, currentUuid]);
  const firstQuestionUuid = useMemo(() => (path.length ? path[0] : null), [path]);

  const setAnswer = (uuid, patch) => {
    setAnswers((prev) => {
      const base = prev[uuid] || {
        uuid,
        type: byId.get(uuid)?.type || "text",
        question: byId.get(uuid)?.question || byId.get(uuid)?.foot_photo || "",
        answer: "",
        file: null,
        geotag: "",
        previewUrl: "",
        needsReattach: false,
      };
      // revoke old preview URL if replacing file
      if (base.previewUrl && patch?.file && base.file && base.file !== patch.file) {
        URL.revokeObjectURL(base.previewUrl);
      }
      const next = { ...base, ...patch };
      if (!next.previewUrl && next.file) {
        try { next.previewUrl = URL.createObjectURL(next.file); } catch { }
      }
      return { ...prev, [uuid]: next };
    });
  };

  useEffect(() => {
    // cleanup previews on unmount/close
    return () => {
      Object.values(answers || {}).forEach((a) => a?.previewUrl && URL.revokeObjectURL(a.previewUrl));
    };
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const g = await captureGeotag();
        setSessionGeo(g);
      } catch (error) {
        console.warn("Geotagging not available:", error);
        setSessionGeo(null); // Explicitly set to null when not available
      }
    })();
    // eslint-disable-next-line
  }, [open]);

  const validateCurrentForNext = () => {
    if (!currentQuestion) return "No question to answer.";
    const isFirst = currentUuid === firstQuestionUuid;
    if (isFirst && !String(answers[currentUuid]?.answer || "").trim()) {
      return "Please answer the first question.";
    }
    const required = String(currentQuestion.require_response || currentQuestion.requireResponse || "")
      .toLowerCase() === "true";
    if (required && (currentQuestion.type === "text" || currentQuestion.type === "dropdown")) {
      if (!String(answers[currentUuid]?.answer || "").trim()) return "This field is required.";
    }
    return "";
  };

  const validateAllBeforeSave = () => {
    if (!path.length) return "No questions to answer.";

    const firstUuid = firstQuestionUuid;
    const firstAns = answers[firstUuid]?.answer;
    if (!String(firstAns || "").trim()) return "Please answer the first question.";

    const firstIsYes = String(firstAns).toLowerCase() === "yes";
    const missing = [];

    for (const uuid of path) {
      const q = byId.get(uuid);
      if (!q) continue;
      const a = answers[uuid];

      if (q.type === "image") {
        // Check if geotagging is available
        const geotaggingAvailable = sessionGeo !== null;

        if (firstIsYes) {
          if (!a?.file) {
            missing.push(`Image required: ${q.foot_photo || q.question || "Image"}`);
          } else if (!adminOverride && !a?.geotag && geotaggingAvailable) {
            // Only require geotag if it's available
            missing.push(`Location required: ${q.foot_photo || q.question || "Image"}`);
          }
        } else {
          if (a?.file && !adminOverride && !a?.geotag && geotaggingAvailable) {
            // Only require geotag if it's available
            missing.push(`Location required: ${q.foot_photo || q.question || "Image"}`);
          }
        }
      }

      const required = String(q.require_response || q.requireResponse || "").toLowerCase() === "true";
      if (required && (q.type === "text" || q.type === "dropdown")) {
        if (!String(a?.answer || "").trim()) {
          missing.push(`Answer required: ${q.question || "Question"}`);
        }
      }
    }

    return missing.length ? missing.join("\n") : "";
  };

  const buildPathFromAnswersAndIndex = () => {
    const nextPath = buildPathFromAnswers(answers);
    const curUuid = currentUuid;
    const idxInNext = nextPath.indexOf(curUuid);
    const nextIdx =
      idxInNext === -1
        ? Math.max(nextPath.length - 1, 0)
        : Math.min(idxInNext + 1, nextPath.length - 1);
    const atEndAfterAnswer = idxInNext >= nextPath.length - 1;
    return { nextPath, nextIdx, atEndAfterAnswer };
  };

  const goNextQuestion = () => {
    const err = validateCurrentForNext();
    if (err) return toast.error(err);
    const { nextPath, nextIdx } = buildPathFromAnswersAndIndex();
    setPath(nextPath);
    setQIdx(nextIdx);
  };

  const goPrevQuestion = () => { if (qIdx > 0) setQIdx(qIdx - 1); };

  // ---------- build FormData (includes tranId) ----------
  const buildFormData = (uid, sid) => {
    const fd = new FormData();
    fd.append("userId", uid);
    fd.append("surveyId", sid);
    fd.append("tranId", tranId);
    fd.append("taskId", getTaskId(currentTask));

    let i = 0;
    for (const uuid of path) {
      const q = byId.get(uuid);
      if (!q) continue;
      const a = answers[uuid] || {};
      fd.append(`answer[${i}][uuid]`, uuid);
      fd.append(`answer[${i}][question]`, q.question || q.foot_photo || "");
      fd.append(`answer[${i}][answer]`, q.type === "image" ? (a.answer || "") : String(a.answer || ""));
      if (q.type === "image" && a.file) {
        fd.append(`answer[${i}][image]`, a.file);
        if (a.geotag) fd.append(`answer[${i}][geotag]`, a.geotag);
      }
      i++;
    }

    if (adminOverride) fd.append("adminOverride", "geotag");
    return fd;
  };

  const onSaveAndNextTask = async () => {
    const err = validateAllBeforeSave();
    if (err) return toast.error(err);

    const uid = getUserIdValue();
    const sid = survey?._id || survey?.id || "";
    if (!uid || !sid || !tranId) return toast.error("Missing required fields (userId / surveyId / tranId).");

    try {
      setSaving(true);
      const fd = buildFormData(uid, sid);
      await axios.post(`${process.env.REACT_APP_API}/answer`, fd, { headers: authHeaders });
      toast.success("Saved");

      // 🔽 remove draft for the just-finished task
      const finishedId = getTaskId(currentTask);
      const nextAnswersByTask = { ...answersByTask };
      delete nextAnswersByTask[finishedId];

      // persist snapshot without this task (keep baseline)
      try {
        const snapshotRaw = localStorage.getItem(keyForProgress(tranId));
        const snapshot = snapshotRaw ? JSON.parse(snapshotRaw) : {};
        snapshot.answersByTask = nextAnswersByTask;
        snapshot.initialIncompleteCount = initialIncompleteCount;
        localStorage.setItem(keyForProgress(tranId), JSON.stringify(snapshot));
      } catch { }

      // refetch remaining tasks so indices line up with server truth
      const { tasks: freshTasks, total } = await fetchAllSurveyTasks();
      setTasks(freshTasks);
      setTotalTasksCount(total || freshTasks.length || 0);
      setAnswersByTask(nextAnswersByTask);

      if (freshTasks.length === 0) {
        toast.success("All survey tasks are completed.");
        // trigger the closing questionnaire
        if (typeof onAllDone === "function") onAllDone();
        // keep modal open until exit if you want, or you can auto-exit:
        // onClose?.(false);
        // Reset view (stay open; only Exit closes)
        setTaskIdx(0);
        setAnswers({});
        setPath([]);
        setQIdx(0);
        return;
      }

      // choose next index safely: keep same index if possible (since current removed)
      const nextIdx = Math.min(taskIdx, freshTasks.length - 1);
      setTaskIdx(nextIdx);

      // load any saved state for that next task, else reset
      const nextId = getTaskId(freshTasks[nextIdx]);
      const saved = nextAnswersByTask[nextId];
      if (saved) {
        const restored = { ...(saved.answers || {}) };
        Object.values(restored).forEach((a) => {
          if (a?.type === "image") {
            delete a.file;
            if (a.previewUrl) { try { URL.revokeObjectURL(a.previewUrl); } catch { } }
            a.previewUrl = "";
            a.answer = "";
            a.needsReattach = true;
          }
        });
        setAnswers(restored);
        setPath(saved.path?.length ? saved.path : computeInitialPathFromTemplate(survey));
        setQIdx(Number.isFinite(saved.qIdx) ? saved.qIdx : 0);
      } else {
        setAnswers({});
        setPath(computeInitialPathFromTemplate(survey));
        setQIdx(0);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Submit failed");
    } finally {
      setSaving(false);
    }
  };

  // ---------- progress (baseline: initialIncompleteCount) ----------
  const progressPercent = useMemo(() => {
    const initial = initialIncompleteCount || 0;
    const remaining = totalTasks || 0; // current incomplete remaining
    if (initial <= 0) return 0;
    const done = Math.max(0, initial - remaining);
    return Math.max(0, Math.min(100, Math.round((done / initial) * 100)));
  }, [initialIncompleteCount, totalTasks]);

  const ctaLabel = useMemo(() => {
    if (!currentQuestion) return "Next";
    const { atEndAfterAnswer } = buildPathFromAnswersAndIndex();
    return atEndAfterAnswer ? "Finish Task" : "Next";
  }, [answers, currentUuid, path]);

  // ---------- render ----------
  const totalPath = path.length;
  const questionNumber = totalPath ? qIdx + 1 : 0;

  const renderControl = (q) => {
    const required = String(q.require_response || q.requireResponse || "").toLowerCase() === "true";

    if (q.type === "dropdown") {
      const opts = Array.isArray(q.options) ? q.options : Object.values(q.options || {});
      return (
        <Select
          style={{ width: "100%" }}
          value={answers[q.uuid]?.answer || undefined}
          onChange={(val) => setAnswer(q.uuid, { answer: val })}
          disabled={isWOCompleted}
          placeholder="Please select"
        >
          {opts.map((v, idx) => (
            <Option key={`${q.uuid}-${idx}`} value={v}>
              {v}
            </Option>
          ))}
        </Select>
      );
    }

    if (q.type === "text") {
      return (
        <Input.TextArea
          rows={3}
          value={answers[q.uuid]?.answer || ""}
          onChange={(e) => setAnswer(q.uuid, { answer: e.target.value })}
          disabled={isWOCompleted}
          placeholder={required ? "Required" : ""}
        />
      );
    }

    if (q.type === "image") {
      const label = q.foot_photo || "Upload Image";
      const a = answers[q.uuid] || {};

      // Check if geotagging is available
      const geotaggingAvailable = sessionGeo !== null;

      const prettyGeo = a.geotag ? (
        <Tag color="blue" style={{ marginTop: 6 }}>
          <AimOutlined /> {Number(String(a.geotag).split(",")[0]).toFixed(5)},{" "}
          {Number(String(a.geotag).split(",")[1]).toFixed(5)}
        </Tag>
      ) : (
        <Tag style={{ marginTop: 6 }}>
          <AimOutlined /> {geotaggingAvailable ? "No geotag yet" : "Geotag unavailable"}
        </Tag>
      );

      const recapture = async () => {
        if (!geotaggingAvailable) {
          toast.error("Geotagging is not available on this device/browser");
          return;
        }

        try {
          const g = await captureGeotag();
          setAnswer(q.uuid, { geotag: `${g.lat},${g.lng}` });
          toast.success("Location captured");
        } catch {
          toast.error("Unable to capture location. Use HTTPS (or localhost) and enable location.");
        }
      };

      return (
        <>
          {a?.needsReattach && (
            <div style={{ marginBottom: 8, color: "#8a8a8a" }}>
              <InfoCircleOutlined /> Image needs re-attach after refresh.
            </div>
          )}

          {!geotaggingAvailable && (
            <Alert
              message="Geotagging unavailable"
              description="Location services are not available on this device or browser. You can still upload images without geotags."
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
            />
          )}

          <Upload.Dragger
            accept="image/*"
            capture="environment"
            multiple={false}
            maxCount={1}
            beforeUpload={async (file) => {
              setAnswer(q.uuid, { file, answer: "Attached", needsReattach: false });

              // Only try to capture geotag if available
              if (geotaggingAvailable) {
                try {
                  const g = sessionGeo || (await captureGeotag());
                  setAnswer(q.uuid, { geotag: `${g.lat},${g.lng}` });
                } catch {
                  toast.error("Location permission denied or failed. Click 'Capture location' to retry.");
                }
              }

              return false; // prevent auto-upload
            }}
            disabled={isWOCompleted}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{label}</p>
            <p className="ant-upload-hint">
              {required ? "Required. " : ""}Tap to choose or take a photo.
              {geotaggingAvailable ? " We'll attach your location." : " Location services unavailable."}
            </p>
          </Upload.Dragger>

          {(a?.previewUrl || a?.geotag) && (
            <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              {a?.previewUrl && (
                <div style={{ position: "relative" }}>
                  <img
                    src={a.previewUrl}
                    alt="preview"
                    style={{ width: 140, height: 140, objectFit: "cover", borderRadius: 8, border: "1px solid #eee" }}
                  />
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {prettyGeo}
                {geotaggingAvailable && (
                  <Tooltip title="Try again to capture GPS coordinates">
                    <Button size="small" icon={<ReloadOutlined />} onClick={recapture} disabled={isWOCompleted}>
                      Capture location
                    </Button>
                  </Tooltip>
                )}
                {a?.file && (
                  <Tooltip title="Remove image">
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        if (a?.previewUrl) URL.revokeObjectURL(a.previewUrl);
                        setAnswer(q.uuid, { file: null, previewUrl: "", geotag: "", answer: "" });
                      }}
                      disabled={isWOCompleted}
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          )}
        </>
      );
    }

    if (q.type === "radio") {
      return (
        <Radio.Group
          value={answers[q.uuid]?.answer || undefined}
          onChange={(e) => setAnswer(q.uuid, { answer: e.target.value })}
          disabled={isWOCompleted}
        >
          <Radio value="Yes">Yes</Radio>
          <Radio value="No">No</Radio>
        </Radio.Group>
      );
    }

    return <Alert type="warning" message={`Unsupported question type: ${q.type}`} />;
  };

  return (
    <Modal
      open={open}
      // hard-block closing by backdrop or ESC/X — only Exit button can close
      maskClosable={false}
      closable={false}
      keyboard={false}
      onCancel={() => { }}
      title="Survey Wizard"
      width={920}
      destroyOnHidden
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <Space>
            {(adminOverrideAllowed || localStorage.getItem("role") === "admin") && (
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={adminOverride}
                  onChange={(e) => setAdminOverride(e.target.checked)}
                  disabled={isWOCompleted}
                />
                Admin override geotag
              </label>
            )}
          </Space>
          <Space>
            <Button onClick={() => onClose(false)}>Exit</Button>
            <Button onClick={() => qIdx > 0 && setQIdx(qIdx - 1)} disabled={qIdx === 0 || path.length === 0}>
              Previous
            </Button>
            <Button
              type="primary"
              loading={saving}
              onClick={() => {
                const err = validateCurrentForNext();
                if (err) return toast.error(err);

                const { nextPath, nextIdx, atEndAfterAnswer } = buildPathFromAnswersAndIndex();

                if (!atEndAfterAnswer) {
                  setPath(nextPath);
                  setQIdx(nextIdx);
                  return;
                }

                const fullErr = validateAllBeforeSave();
                if (fullErr) return toast.error(fullErr);
                onSaveAndNextTask();
              }}
              disabled={isWOCompleted || !currentTask || !currentQuestion}
            >
              {(() => {
                if (!currentQuestion) return "Next";
                const { atEndAfterAnswer } = buildPathFromAnswersAndIndex();
                return atEndAfterAnswer ? "Finish Task" : "Next";
              })()}
            </Button>
          </Space>
        </div>
      }
    >
      {loadingInit ? (
        <div style={{ padding: 16 }}>
          <Spin />
        </div>
      ) : !currentTask ? (
        <Alert type="info" message="No tasks to complete for this work order." showIcon />
      ) : !survey?.customSurvey?.length ? (
        <Alert type="warning" message="No survey configured for this job type." showIcon />
      ) : totalTasks === 0 && initialIncompleteCount === 0 ? (
        <Alert type="info" message="No tasks available." showIcon />
      ) : path.length === 0 ? (
        <Alert type="info" message="No active questions in this template." showIcon />
      ) : (
        <>
          {/* Header status */}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                Task {Math.min(completedSoFar + 1, Math.max(initialIncompleteCount, 1))} of {Math.max(initialIncompleteCount, 1)} — {currentTask?.title || "Untitled"} <strong className="text-primary">#{currentTask?.serialNumber || ""}</strong>
              </div>
              <div style={{ color: "#666", fontSize: 12 }}>
                (Server total: {totalTasksCount || totalTasks} tasks; showing {totalTasks} incomplete)
              </div>
            </div>
            {initialIncompleteCount > 0 && (
              <div style={{ minWidth: 240 }}>
                <Progress
                  status="active"
                  percent={progressPercent}
                  showInfo
                  format={() => `Completed ${completedSoFar} of ${initialIncompleteCount}`}
                />

              </div>
            )}
          </div>

          {/* Question progress */}
          <div style={{ marginBottom: 8, color: "#666" }}>
            Question {questionNumber} of {path.length}
          </div>

          {/* Current question */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              {currentQuestion?.question || currentQuestion?.foot_photo || "Question"}
              {String(currentQuestion?.require_response || currentQuestion?.requireResponse || "")
                .toLowerCase() === "true" && <span style={{ color: "#d00", marginLeft: 6 }}>*</span>}
            </div>
            {renderControl(currentQuestion)}
          </div>
        </>
      )}
    </Modal>
  );
}

